import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { css } from "@emotion/react";
import { useMutation } from "@apollo/client";

import { useAppState } from "core/contexts/AppContext";
import { USER_ONLINE_VIDEO_ACTIVITY_MUTATION } from "core/includes/queries";
import SegmentedVideoItem from "elements/Components/SegmentedVideo/SegmentedVideoItem";

type Props = {
    data: SegmentedVideo,
};

const SegmentedVideo: React.FC<Props> = ({ data }) => {
    const navigate = useNavigate();
    const [appState] = useAppState();
    const [videoItems, setVideoItems] = useState<SegmentedVideoItem[]>([]);
    const params = useParams();
    const onlineEventUrlSegment = params.onlineEventUrlSegment;
    const segmentedVideoItems = data.videoItems;

    /**
     * Get the available video-UrlSegment to be shown.
     *
     * (Path rule: /:onlineEventUrlSegment/:segmentedVideoUrlSegment")
     * Case 1: If it has video-UrlSegment, it returns that videoUrlSegment.
     * Case 2: If it has UserId and Online-Event-UrlSegment (doesn't have video-UrlSegment), it gets the latest watched
     *         video and returns the next video-RulSegment. If the latest video is the last video, it returns its
     *         UrlSegment.
     * Case 3: If else (doesn't have UserId and video-UrlSegment), it returns the first video-UrlSegment.
     */
    const videoUrlSegment = useMemo(() => {
        if (params.segmentedVideoUrlSegment) {
            return params.segmentedVideoUrlSegment;
        }

        if (appState.user?.id && segmentedVideoItems) {
            const watchedVideos = segmentedVideoItems.filter(
                (videoItem: SegmentedVideoItem) => videoItem.isWatched
            );

            if (watchedVideos) {
                const lastWatchedVideoIndex = segmentedVideoItems.findIndex(
                    (videoItem: SegmentedVideoItem) => videoItem.id === watchedVideos[watchedVideos.length - 1]?.id
                );

                if (lastWatchedVideoIndex === segmentedVideoItems.length - 1) {
                    return segmentedVideoItems[segmentedVideoItems.length - 1].urlSegment;
                } else {
                    return segmentedVideoItems[lastWatchedVideoIndex + 1].urlSegment;
                }
            }
        }

        return segmentedVideoItems[0]?.urlSegment;
    }, [params.segmentedVideoUrlSegment, appState.user, segmentedVideoItems]);

    const [updateUserOnlineVideoActivity] = useMutation(USER_ONLINE_VIDEO_ACTIVITY_MUTATION);

    useEffect(() => {
        if (segmentedVideoItems) {
            setVideoItems(
                segmentedVideoItems.map(
                    (videoItem: SegmentedVideoItem) => ({ ...videoItem })
                )
            );
        }
    }, [segmentedVideoItems, setVideoItems]);

    const currentVideoItem = useMemo(() => (
        videoItems?.find(
            (videoItem: SegmentedVideoItem) => videoItem.urlSegment === videoUrlSegment
        )
    ), [videoItems, videoUrlSegment]);

    const currentIndex = useMemo(() => (
        currentVideoItem ? videoItems.findIndex((videoItem: SegmentedVideoItem) => videoItem.id === currentVideoItem.id) : -1
    ), [currentVideoItem, videoItems]);

    const hasNextVideo = useMemo(() => {
        if (!currentVideoItem) return false;

        return currentIndex < (videoItems.length - 1);
    }, [currentIndex, currentVideoItem, videoItems.length]);

    const onVideoClickHandler = useCallback((urlSegment: string) => {
        if (currentVideoItem?.urlSegment !== urlSegment) {
            navigate(`/online-event/${onlineEventUrlSegment}/${urlSegment}`);
        }
    }, [currentVideoItem?.urlSegment, onlineEventUrlSegment, navigate]);

    const onNextVideo = useCallback(() => {
        if (currentVideoItem && hasNextVideo) {
            const targetedIndex = currentIndex+ 1;
            onVideoClickHandler(videoItems[targetedIndex].urlSegment);
        }
    }, [
        currentIndex,
        currentVideoItem,
        hasNextVideo,
        onVideoClickHandler,
        videoItems
    ]);

    const onAttemptVideo = useCallback(() => {
        if (!currentVideoItem || !appState.user?.id || currentVideoItem.isWatched) {
            return;
        }

        const variables = {
            userId: appState.user.id,
            onlineEventUrlSegment,
            segmentedVideoItemId: currentVideoItem.id,
        };

        updateUserOnlineVideoActivity({ variables }).then(() => {
            videoItems[currentIndex].isWatched = true;
        });
    }, [
        currentVideoItem,
        appState.user,
        onlineEventUrlSegment,
        videoItems,
        currentIndex,
        updateUserOnlineVideoActivity,
    ]);

    const style = css`
        margin: 20px 0;
        width: 100%;
        height: 100%;
    `;

    return (
        <div css={style}>
            {currentVideoItem && (
                <SegmentedVideoItem
                    videoItems={videoItems}
                    currentVideoItem={currentVideoItem}
                    themeColour={data.themeColour}
                    hasNextVideo={hasNextVideo}
                    onVideoStart={onAttemptVideo}
                    onNextVideo={onNextVideo}
                    onChangeCurrentTopic={onVideoClickHandler}
                />
            )}
        </div>
    );
};

export default SegmentedVideo;